<script setup lang="ts">
import { computed, inject, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { FormInstance, FormRules } from 'element-plus';
import { ArrowRight, Lock as LockIcon, OfficeBuilding, User } from '@element-plus/icons-vue';

import { LOGIN_DEFAULT_TENANT_CODE_KEY, useDeploymentMode } from '../composables/useDeploymentMode';
import {
  LOGIN_TENANT_CODE_ALL,
  resolveLoginRouteTenantState,
  resolvePostLoginRedirect,
  tenantCodeForLoginRequest,
  tenantPath,
} from '../router/login-route';
import { LoginError, useAuthStore } from '../stores/auth';

/**
 * 로그인 화면 (E.3.4).
 *
 * 폼: docs/04-authentication.md §3.1 (username, password, tenantCode).
 * - SaaS: 테넌트는 URL `/login/{tenantCode}` 로 고정. `all` 이면 입력 필드 표시.
 * - On-prem: URL 세그먼트와 무관하게 주입된 기본 코드만 사용.
 */

const deploymentMode = useDeploymentMode();
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const loginDefaultTenantCode = inject(LOGIN_DEFAULT_TENANT_CODE_KEY, 'platform');

interface LoginForm {
  username: string;
  password: string;
  tenantCode: string;
}

const form = reactive<LoginForm>({
  username: '',
  password: '',
  tenantCode: loginDefaultTenantCode,
});

const loginTenantState = computed(() =>
  resolveLoginRouteTenantState(route.params.tenantCode, {
    mode: deploymentMode,
    defaultTenantCode: loginDefaultTenantCode,
  }),
);

const showTenantCodeField = computed(() => loginTenantState.value.showTenantCodeField);

watch(
  loginTenantState,
  (state) => {
    if (state.showTenantCodeField) return;
    if (state.urlTenantCode && state.urlTenantCode !== LOGIN_TENANT_CODE_ALL) {
      form.tenantCode = state.urlTenantCode;
    }
  },
  { immediate: true },
);

const formRef = ref<FormInstance>();
const submitting = ref(false);
const serverError = ref<LoginError | null>(null);

const rules = computed<FormRules<LoginForm>>(() => {
  const base: FormRules<LoginForm> = {
    username: [{ required: true, message: '아이디를 입력해 주세요', trigger: 'blur' }],
    password: [{ required: true, message: '비밀번호를 입력해 주세요', trigger: 'blur' }],
  };
  if (showTenantCodeField.value) {
    base.tenantCode = [{ required: true, message: '테넌트 코드를 입력해 주세요', trigger: 'blur' }];
  }
  return base;
});

async function handleSubmit(): Promise<void> {
  const formInstance = formRef.value;
  if (!formInstance) return;

  serverError.value = null;

  const tenantCodeForRequest = tenantCodeForLoginRequest(loginTenantState.value, {
    mode: deploymentMode,
    defaultTenantCode: loginDefaultTenantCode,
    formTenantCode: form.tenantCode,
  });

  const valid = await formInstance.validate().catch(() => false);
  if (!valid) return;

  submitting.value = true;
  try {
    await authStore.login({
      username: form.username,
      password: form.password,
      tenantCode: tenantCodeForRequest,
    });
    const tenantCode = authStore.user?.tenantCode ?? '';
    if (authStore.user?.mustChangePassword) {
      await router.push(tenantPath('/account/settings', tenantCode));
      return;
    }
    // redirect 는 가드가 보존한 경로 — prefix 포함(테넌트 일치 시) 또는 top-level
    // 인증 라우트(예: MES /pop/*)만 원경로 복귀, 그 외는 대시보드 강등(F-2~F-4).
    const redirect = resolvePostLoginRedirect({
      redirectQuery: route.query.redirect,
      tenantCode,
      probe: (path) => {
        const resolved = router.resolve(path);
        return {
          isNotFound: resolved.name === 'not-found',
          isTenantSubtree: resolved.params.tenantCode !== undefined,
        };
      },
    });
    await router.push(redirect);
  } catch (err: unknown) {
    serverError.value =
      err instanceof LoginError
        ? err
        : new LoginError('unknown', err instanceof Error ? err.message : String(err));
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <el-form
    ref="formRef"
    class="login-form"
    :model="form"
    :rules="rules"
    label-position="top"
    @submit.prevent="handleSubmit"
  >
    <el-alert
      v-if="serverError"
      :title="serverError.message"
      :type="serverError.kind === 'account_locked' ? 'warning' : 'error'"
      show-icon
      :closable="false"
      class="login-alert"
    />

    <el-form-item label="아이디" prop="username">
      <el-input
        v-model="form.username"
        name="username"
        autocomplete="username"
        size="large"
        :prefix-icon="User"
        :disabled="submitting"
      />
    </el-form-item>

    <el-form-item label="비밀번호" prop="password">
      <el-input
        v-model="form.password"
        type="password"
        name="password"
        autocomplete="current-password"
        show-password
        size="large"
        :prefix-icon="LockIcon"
        :disabled="submitting"
        @keyup.enter="handleSubmit"
      />
    </el-form-item>

    <el-form-item v-if="showTenantCodeField" label="테넌트 코드" prop="tenantCode">
      <el-input
        v-model="form.tenantCode"
        name="tenantCode"
        size="large"
        :prefix-icon="OfficeBuilding"
        :disabled="submitting"
        placeholder="예: platform"
      />
    </el-form-item>

    <el-form-item>
      <el-button
        type="primary"
        native-type="submit"
        :loading="submitting"
        :icon="ArrowRight"
        class="login-submit"
      >
        로그인
      </el-button>
    </el-form-item>
  </el-form>
</template>

<style scoped>
.login-form :deep(.el-form-item) {
  margin-bottom: 18px;
}

.login-form :deep(.el-form-item__label) {
  margin-bottom: 7px;
  color: var(--el-text-color-regular);
  font-weight: 700;
}

.login-form :deep(.el-input__wrapper) {
  border-radius: 8px;
  overflow: hidden;
  background: var(--el-fill-color-blank);
  box-shadow: 0 0 0 1px var(--el-border-color) inset;
}

.login-form :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px var(--ax-color-primary) inset;
}

.login-form :deep(.el-input__inner) {
  background: transparent;
}

.login-form :deep(.el-input__inner:-webkit-autofill),
.login-form :deep(.el-input__inner:-webkit-autofill:hover),
.login-form :deep(.el-input__inner:-webkit-autofill:focus) {
  box-shadow: 0 0 0 1000px var(--el-fill-color-blank) inset;
  caret-color: var(--el-text-color-primary);
  -webkit-text-fill-color: var(--el-text-color-primary);
}

.login-submit {
  width: 100%;
  height: 44px;
  margin-top: 6px;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(
    135deg,
    var(--ax-color-primary),
    color-mix(in srgb, var(--ax-color-secondary) 72%, var(--ax-color-primary))
  );
  font-weight: 800;
  box-shadow: 0 14px 28px color-mix(in srgb, var(--ax-color-primary) 28%, transparent);
}

.login-alert {
  margin-bottom: 16px;
}
</style>
